import { Directive, ElementRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { ConfirmationService } from 'primeng/api'
import { FormControl } from '@angular/forms'

@Directive()
export abstract class InlineEditCellDirective implements OnChanges {
  @Input() fieldName: string
  @Input() rowData: any
  @Input() editable: boolean
  @Input() mandatory: boolean
  @Input() customEdit = false
  // min width of the cell in edit mode. Used to determine whether
  @Input() minWidthPx = 70

  @Output() completeCellEdit: EventEmitter<any> = new EventEmitter()
  @Output() onCustomEdit: EventEmitter<any> = new EventEmitter()

  newValue: any | FormControl | undefined
  originalContent: Object
  isEdited = false

  private confirmationService = inject(ConfirmationService)
  private el = inject(ElementRef)

  abortCellEdit(event) {
    // As of primeng 15.2 we have no way of correlating a colorpicker dialog that has been attached to the document body
    // with the control we defiend in the template. We thus need to check each event for the class name of the target.
    // Clicking the colorpicker should not abort the cell edit, however we have no way of knowing whether the clicked
    // colorpicker actually belongs to the cell being edited. In most cases this should not cause problems...
    if (event?.target?.className?.includes('p-colorpicker')) {
      return
    }

    if (!event || !event.relatedTarget || event.relatedTarget['id'] !== this.fieldName + '§' + this.rowData['id']) {
      this.rowData[this.fieldName] = this.originalContent
    } else {
      this.completeCellEdit.emit({ data: this.rowData, field: this.fieldName })
    }

    this.isEdited = false
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.editable === undefined && !changes.editable) {
      this.editable = true
    }
    this.isEdited = false
    this.originalContent = null
  }

  onDelete(onAccept?: Function) {
    this.confirmationService.confirm({
      message: 'Sind Sie sicher, dass Sie dieses Feld leeren wollen?',
      header: 'Feld leeren',
      key: 'global',
      accept: () => {
        // new refactored components wrap input controls in a formControl. Resetting the value happens with reset().
        // old components hold the value directly and just need to be set to undefined
        if (this.newValue instanceof FormControl) {
          this.newValue.reset()
        } else {
          this.newValue = undefined
        }
        this.rowData[this.fieldName] = null
        this.completeCellEdit.emit({ data: this.rowData, field: this.fieldName })
        if (!!onAccept) {
          onAccept()
        }
      }
    })
  }

  onEdit() {
    if (this.customEdit) {
      this.onCustomEdit.emit()
    } else {
      this.triggerEdit()
    }
  }

  showInputAsOverlay(minWidthPx: number): boolean {
    const parentElement = <HTMLElement>this.el.nativeElement.parentElement
    const availableWidthPx = parentElement.getBoundingClientRect().width
    return availableWidthPx < minWidthPx
  }

  abstract triggerEdit(): void
}
